/*
 * http://jasonwatmore.com/post/2018/10/29/angular-7-user-registration-and-login-example-tutorial
 * 
 * The login component uses the authentication service to login to the application. If the user is already logged in they are automatically redirected to the home page.
 *
 * The loginForm: FormGroup object defines the form controls and validators, and is used to access data entered into the form.
 * The FormGroup is part of the Angular Reactive Forms module and is bound to the login template above with the [formGroup]="loginForm" directive.
*/
import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { first } from 'rxjs/operators';
import { PersistenceService } from '@app/_services/persistence.service';
import { BaseComponent } from '@app/base/basecomponent';
import { UserSettingService } from '@app/areas/usersetting/usersetting.service';
import { UserSettingLink } from '@app/_models/settings/user-settings';
import { UserLanguage } from '@app/_enums/userLanguage';
import { MiscellaneousService } from '@app/_services/miscellaneous.service';
import jwt_decode from 'jwt-decode';

@Component({
  templateUrl: 'login-form.component.html',
})
export class LoginComponent extends BaseComponent implements OnInit {
  public loginForm: UntypedFormGroup;
  public loading = false;
  public returnUrl: string;

  get f() { return this.loginForm.controls; }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private persistence: PersistenceService,
    private userSettingService: UserSettingService,
    private miscService: MiscellaneousService
  ) {
    super();

    // redirect to home if already logged in
    if (this.authService.currentUserValue) {
      this.router.navigate(['/']);
    }
  }

  ngOnInit() {
    //this.translate.setDefaultLang('fi');
    this.translate.use(this.persistence.getLang());

    this.loginForm = this.formBuilder.group({
      username: [null, Validators.required],
      password: [null, Validators.required],
      rememberMe: [null]
    });

    // reset login status
    //this.authService.logout();

    // get return url from route parameters or default to '/'
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';

    if (localStorage.getItem("rememberme") !== null) {
      this.f.username.setValue(localStorage.getItem("username"));
      this.f.rememberMe.patchValue(true);
    }

  }

  onSubmit() {
    if (!this.loginForm.valid) {
      this.formError(this.loginForm, 'login.errors.login_form');
      return;
    }

    this.loading = true;
    this.authService.login(this.f.username.value, this.f.password.value).pipe(first()).subscribe(user => {

      this.loading = false;

      // Change language if it differs from current language. Note that it is also possible to simply call POST request to
      // change it even when it has not been changed.
      this.miscService.getUserLanguage().subscribe((userLanguage) => {
        const currentLang = localStorage.getItem('l-curLang');
        if(currentLang && currentLang !== userLanguage){
          let langSettings: UserSettingLink[] = [{
            userAccountId: user.id,
            userSettingId: "userLanguage",
            currentVal: UserLanguage[currentLang]
          }];

          this.userSettingService.setUserSettings(langSettings).subscribe();
        }
      });

      // When this local storage item is set, local storage items should be refreshed.
      this.persistence.set('avux-req-init', 1);

      if (this.f.rememberMe.value) {
        localStorage.setItem('rememberme', JSON.stringify(this.f.rememberMe.value));
        localStorage.setItem('username', this.f.username.value);
      } else {
        localStorage.removeItem('rememberme');
        localStorage.removeItem('username');
      }
      if (this.authService.currentUserValue.multiTenantUser) {
        this.router.navigate(['/org-login']);
      } else {
        this.router.navigate([this.returnUrl]);
      }

    }, (error) => {
      if (error.status && error.status == 403) {
        this.alertService.error("login.errors.invalid_credentials", null, true, 4000, true);
      } else {
        this.alertService.error(error.error.description || "common.errors.unknown");
      }

      this.loading = false;
    });
  }

  /**
   * Remove all expired jw-tokens from local storage. 
   */
  removeExpiredTokens() {
    // Get all keys from the localStorage
    const localStorageKeys = Object.keys(localStorage);
  
    // Filter the keys to find the ones starting with "currentUser_"
    const currentUserKeys = localStorageKeys.filter(key => key.startsWith('currentUser_'));
  
    for (const key of currentUserKeys) {
      const item = JSON.parse(localStorage.getItem(key));

      if (item && item.token && this.isTokenExpired(item.token)) {
        localStorage.removeItem(key);
      }
    }
  }
  
  isTokenExpired(token: string): boolean {
    const decodedToken = this.decodeToken(token);
    const expirationDate = new Date(decodedToken.exp * 1000);
  
    return expirationDate < new Date();
  }
  
  decodeToken(token: string): any {
    return jwt_decode(token);
  }
}
